/**
 * Mock data shared by the /contact-preview page so each activity tab can
 * render a populated example without spinning up Apollo MockedProvider.
 *
 * Each tab checks `contactId.startsWith(PREVIEW_PREFIX)` and, when true,
 * uses these arrays in place of its GraphQL fetch. Lets visual-QA reviewers
 * see realistic content in every tab.
 */

export const PREVIEW_PREFIX = 'preview-mock-';

export const isPreviewContactId = (id?: string): boolean =>
  Boolean(id && id.startsWith(PREVIEW_PREFIX));

/* ── Recent Activities feed ─────────────────────────────────────── */
export const PREVIEW_ACTIVITY_FEED = [
  {
    eventId: 'evt-1',
    eventType: 'appointment_set',
    eventAt: '2026-05-16T16:00:00Z',
    title: 'Appointment set · Saturday 11am tour',
    summary: 'Lead confirmed Saturday tour at 142 Maple. Pull comps for 89 Elm same day.',
    channel: 'sms',
    direction: 'inbound',
    outcome: 'appointment_set',
    callId: null,
    durationSeconds: null,
    sentiment: 'positive',
    metadata: '{}',
  },
  {
    eventId: 'evt-2',
    eventType: 'call',
    eventAt: '2026-05-15T14:22:00Z',
    title: 'Outbound call · 8 min',
    summary: 'Remi confirmed buyer intent, $650-750k budget, Boston preferred neighborhoods.',
    channel: 'outbound',
    direction: 'outbound',
    outcome: 'qualifier_complete',
    callId: 'call-mock-2',
    durationSeconds: 482,
    sentiment: 'positive',
    metadata: '{}',
  },
  {
    eventId: 'evt-3',
    eventType: 'email',
    eventAt: '2026-05-13T09:18:00Z',
    title: 'Email opened · "Your Saturday tour at 142 Maple"',
    summary: 'Lead opened tour confirmation email twice, clicked map link.',
    channel: 'email',
    direction: 'outbound',
    outcome: 'engaged',
    callId: null,
    durationSeconds: null,
    sentiment: 'positive',
    metadata: '{}',
  },
  {
    eventId: 'evt-4',
    eventType: 'property_view',
    eventAt: '2026-05-12T20:44:00Z',
    title: 'Saved 142 Maple Street, Boston',
    summary: 'Lead viewed the listing 4 times and saved it. Spent 6 min on photos.',
    channel: 'website',
    direction: 'inbound',
    outcome: 'engaged',
    callId: null,
    durationSeconds: null,
    sentiment: 'positive',
    metadata: '{}',
  },
  {
    eventId: 'evt-5',
    eventType: 'sms',
    eventAt: '2026-05-10T17:02:00Z',
    title: 'Inbound SMS · "Yes still interested"',
    summary: 'Lead replied to nurture cadence — actively engaged.',
    channel: 'sms',
    direction: 'inbound',
    outcome: 'engaged',
    callId: null,
    durationSeconds: null,
    sentiment: 'positive',
    metadata: '{}',
  },
  {
    eventId: 'evt-6',
    eventType: 'call',
    eventAt: '2026-05-07T11:42:00Z',
    title: 'First voice contact · 12 min',
    summary:
      'Remi qualifier call. Buyer, preapproved $725k, flexible close window (Jun 30 – Jul 30).',
    channel: 'outbound',
    direction: 'outbound',
    outcome: 'qualifier_complete',
    callId: 'call-mock-1',
    durationSeconds: 723,
    sentiment: 'positive',
    metadata: '{}',
  },
  {
    eventId: 'evt-7',
    eventType: 'registration',
    eventAt: '2026-05-04T08:30:00Z',
    title: 'Registered on the IDX site',
    summary: 'Lead registered from a Zillow PPC ad — buyer in 02116.',
    channel: 'website',
    direction: 'inbound',
    outcome: 'lead_captured',
    callId: null,
    durationSeconds: null,
    sentiment: 'neutral',
    metadata: '{}',
  },
];

/* ── Calls tab ──────────────────────────────────────────────────── */
export const PREVIEW_CALLS = [
  {
    id: 'call-mock-3',
    call_id: 'call-mock-3',
    inbound: false,
    from_number: '+16175550199',
    to_number: '+16175550148',
    callTimestamp: '2026-05-16T13:05:00Z',
    call_length: 8.2,
    duration: 492,
    answered_by: 'human',
    call_analysis: {
      outcome: 'appointment_set',
      summary:
        'Sarah confirmed the Saturday 11am tour at 142 Maple. Lead asked about parking.',
      timeline: undefined,
      answers: undefined,
    },
    concatenated_transcript: undefined,
    recordingUrl: undefined,
    sentiment: 'positive',
    isTest: false,
    flowExecution: null,
  },
  {
    id: 'call-mock-2',
    call_id: 'call-mock-2',
    inbound: false,
    from_number: '+16175550101',
    to_number: '+16175550148',
    callTimestamp: '2026-05-15T14:22:00Z',
    call_length: 8.0,
    duration: 482,
    answered_by: 'human',
    call_analysis: {
      outcome: 'qualifier_complete',
      summary:
        'Remi confirmed buyer intent, $650-750k, south-end preferred, parking required.',
      timeline: undefined,
      answers: undefined,
    },
    concatenated_transcript: undefined,
    recordingUrl: undefined,
    sentiment: 'positive',
    isTest: false,
    flowExecution: null,
  },
  {
    id: 'call-mock-1',
    call_id: 'call-mock-1',
    inbound: false,
    from_number: '+16175550101',
    to_number: '+16175550148',
    callTimestamp: '2026-05-07T11:42:00Z',
    call_length: 12.0,
    duration: 723,
    answered_by: 'human',
    call_analysis: {
      outcome: 'qualifier_complete',
      summary:
        'First voice contact. Captured budget ($650-750k), intent (buyer), preferred neighborhoods.',
      timeline: undefined,
      answers: undefined,
    },
    concatenated_transcript: undefined,
    recordingUrl: undefined,
    sentiment: 'positive',
    isTest: false,
    flowExecution: null,
  },
];

/* ── Emails tab ─────────────────────────────────────────────────── */
export const PREVIEW_EMAILS = [
  {
    emailId: 'email-mock-3',
    subject: 'Your Saturday tour at 142 Maple',
    fromAddress: 'sarah@mendozagroup.com',
    toAddress: 'alessandra.hawthorne@longtailexample.com',
    eventType: 'opened',
    direction: 'outbound',
    sentAt: '2026-05-13T09:18:00Z',
    receivedAt: null,
    openedAt: '2026-05-13T09:21:00Z',
    clickedAt: '2026-05-13T09:22:00Z',
  },
  {
    emailId: 'email-mock-2',
    subject: 'Comps for 89 Elm + 142 Maple',
    fromAddress: 'sarah@mendozagroup.com',
    toAddress: 'alessandra.hawthorne@longtailexample.com',
    eventType: 'sent',
    direction: 'outbound',
    sentAt: '2026-05-09T17:30:00Z',
    receivedAt: null,
    openedAt: '2026-05-09T18:02:00Z',
    clickedAt: null,
  },
  {
    emailId: 'email-mock-1',
    subject: 'Welcome to Mendoza Group',
    fromAddress: 'remi@mendozagroup.com',
    toAddress: 'alessandra.hawthorne@longtailexample.com',
    eventType: 'delivered',
    direction: 'outbound',
    sentAt: '2026-05-04T08:32:00Z',
    receivedAt: null,
    openedAt: '2026-05-04T08:45:00Z',
    clickedAt: null,
  },
];

/* ── Website activity tab ───────────────────────────────────────── */
export const PREVIEW_WEB_ACTIVITY = [
  {
    eventId: 'web-mock-5',
    eventType: 'property_saved',
    eventAt: '2026-05-12T20:44:00Z',
    eventData: {
      address: '142 Maple Street, Boston, MA 02116',
      price: 845000,
      mlsNumber: '73215987',
    },
    device: 'desktop',
    sessionId: 'session-mock-3',
    pageUrl: '/listing/73215987',
  },
  {
    eventId: 'web-mock-4',
    eventType: 'property_view',
    eventAt: '2026-05-11T19:12:00Z',
    eventData: {
      address: '89 Elm Street, Boston, MA 02116',
      price: 795000,
      mlsNumber: '73215321',
    },
    device: 'mobile',
    sessionId: 'session-mock-2',
    pageUrl: '/listing/73215321',
  },
  {
    eventId: 'web-mock-3',
    eventType: 'search',
    eventAt: '2026-05-11T19:08:00Z',
    eventData: {
      searchQuery: 'south end boston 2br',
      resultsCount: 14,
    },
    device: 'mobile',
    sessionId: 'session-mock-2',
    pageUrl: '/search',
  },
  {
    eventId: 'web-mock-2',
    eventType: 'property_view',
    eventAt: '2026-05-08T15:30:00Z',
    eventData: {
      address: '14 Beacon Hill Pl, Boston, MA 02114',
      price: 920000,
      mlsNumber: '73214900',
    },
    device: 'desktop',
    sessionId: 'session-mock-1',
    pageUrl: '/listing/73214900',
  },
  {
    eventId: 'web-mock-1',
    eventType: 'registration',
    eventAt: '2026-05-04T08:30:00Z',
    eventData: {
      source: 'zillow-ppc',
    },
    device: 'desktop',
    sessionId: 'session-mock-1',
    pageUrl: '/register',
  },
];

/* ── Saved Properties tab ───────────────────────────────────────── */
export const PREVIEW_SAVED_PROPERTIES = [
  {
    propertyId: 'prop-mock-1',
    mlsNumber: '73215987',
    mlsBoardCode: 'MLSPIN',
    listingStatus: 'Active',
    price: 845000,
    listPrice: 845000,
    address: '142 Maple Street',
    city: 'Boston',
    state: 'MA',
    zip: '02116',
    bedrooms: 4,
    bathrooms: 2.5,
    squareFeet: 2240,
    propertyType: 'Single Family',
    photoUrl: null,
    savedAt: '2026-05-12T20:44:00Z',
  },
  {
    propertyId: 'prop-mock-2',
    mlsNumber: '73215321',
    mlsBoardCode: 'MLSPIN',
    listingStatus: 'Active',
    price: 795000,
    listPrice: 795000,
    address: '89 Elm Street',
    city: 'Boston',
    state: 'MA',
    zip: '02116',
    bedrooms: 3,
    bathrooms: 2,
    squareFeet: 1950,
    propertyType: 'Townhouse',
    photoUrl: null,
    savedAt: '2026-05-11T19:14:00Z',
  },
  {
    propertyId: 'prop-mock-3',
    mlsNumber: '73214900',
    mlsBoardCode: 'MLSPIN',
    listingStatus: 'Pending',
    price: 920000,
    listPrice: 920000,
    address: '14 Beacon Hill Pl',
    city: 'Boston',
    state: 'MA',
    zip: '02114',
    bedrooms: 3,
    bathrooms: 2.5,
    squareFeet: 2100,
    propertyType: 'Condo',
    photoUrl: null,
    savedAt: '2026-05-08T15:31:00Z',
  },
];

/* ── SMS tab ────────────────────────────────────────────────────── */
export const PREVIEW_SMS = [
  {
    smsId: 'sms-mock-1',
    direction: 'outbound',
    fromNumber: '+16175550199',
    fromName: 'Remi',
    toNumber: '+16175550148',
    body: "Hi Alessandra, this is Remi from Mendoza Group. Saw you registered yesterday — got a quick question about your search to make sure I send you the right listings. Are you looking in Boston specifically or also open to Brookline / Cambridge?",
    sentAt: '2026-05-04T08:35:00Z',
    deliveredAt: '2026-05-04T08:35:08Z',
    readAt: '2026-05-04T08:42:00Z',
  },
  {
    smsId: 'sms-mock-2',
    direction: 'inbound',
    fromNumber: '+16175550148',
    fromName: 'Alessandra',
    toNumber: '+16175550199',
    body: "Hi! Boston preferred — south end ideally. Open to Brookline if it's close to the Orange Line.",
    sentAt: '2026-05-04T08:44:00Z',
    deliveredAt: '2026-05-04T08:44:02Z',
    readAt: '2026-05-04T08:44:30Z',
  },
  {
    smsId: 'sms-mock-3',
    direction: 'outbound',
    fromNumber: '+16175550199',
    fromName: 'Remi',
    toNumber: '+16175550148',
    body: "Perfect. 2br minimum? And do you need parking?",
    sentAt: '2026-05-04T08:45:00Z',
    deliveredAt: '2026-05-04T08:45:03Z',
    readAt: '2026-05-04T08:45:40Z',
  },
  {
    smsId: 'sms-mock-4',
    direction: 'inbound',
    fromNumber: '+16175550148',
    fromName: 'Alessandra',
    toNumber: '+16175550199',
    body: "Yes 2br min. Parking required, ideally garage.",
    sentAt: '2026-05-04T08:46:30Z',
    deliveredAt: '2026-05-04T08:46:32Z',
    readAt: '2026-05-04T08:47:00Z',
  },
  {
    smsId: 'sms-mock-5',
    direction: 'outbound',
    fromNumber: '+16175550199',
    fromName: 'Remi',
    toNumber: '+16175550148',
    body: "Great. I'll send a curated list tonight. One more — budget ceiling?",
    sentAt: '2026-05-04T08:48:00Z',
    deliveredAt: '2026-05-04T08:48:02Z',
    readAt: '2026-05-04T08:51:00Z',
  },
  {
    smsId: 'sms-mock-6',
    direction: 'inbound',
    fromNumber: '+16175550148',
    fromName: 'Alessandra',
    toNumber: '+16175550199',
    body: "$725k is the preapproval limit — would prefer $650-700 range.",
    sentAt: '2026-05-04T08:52:00Z',
    deliveredAt: '2026-05-04T08:52:01Z',
    readAt: '2026-05-04T08:52:25Z',
  },
  {
    smsId: 'sms-mock-7',
    direction: 'outbound',
    fromNumber: '+16175550199',
    fromName: 'Remi',
    toNumber: '+16175550148',
    body: "Got it. I'll have something to you by 8pm. Thanks!",
    sentAt: '2026-05-04T08:53:00Z',
    deliveredAt: '2026-05-04T08:53:03Z',
    readAt: '2026-05-04T08:53:20Z',
  },
  {
    smsId: 'sms-mock-8',
    direction: 'outbound',
    fromNumber: '+16175550199',
    fromName: 'Sarah Mendoza-Patel',
    toNumber: '+16175550148',
    body: "Hi Alessandra — Sarah from Mendoza Group. Remi looped me in on your search. Loved the 142 Maple listing you saved — want to tour Saturday? I have 10:30 or 11am.",
    sentAt: '2026-05-12T21:00:00Z',
    deliveredAt: '2026-05-12T21:00:02Z',
    readAt: '2026-05-12T21:14:00Z',
  },
  {
    smsId: 'sms-mock-9',
    direction: 'inbound',
    fromNumber: '+16175550148',
    fromName: 'Alessandra',
    toNumber: '+16175550199',
    body: "11am works perfect! Should I meet you there?",
    sentAt: '2026-05-12T21:16:00Z',
    deliveredAt: '2026-05-12T21:16:01Z',
    readAt: '2026-05-12T21:16:30Z',
  },
  {
    smsId: 'sms-mock-10',
    direction: 'outbound',
    fromNumber: '+16175550199',
    fromName: 'Sarah Mendoza-Patel',
    toNumber: '+16175550148',
    body: "Perfect — see you at 142 Maple at 11am Saturday. I'll bring comps for 89 Elm too.",
    sentAt: '2026-05-12T21:17:30Z',
    deliveredAt: '2026-05-12T21:17:32Z',
    readAt: '2026-05-12T21:18:00Z',
  },
];

/* ── Home Valuations tab ────────────────────────────────────────── */
export const PREVIEW_VALUATIONS = [
  {
    valuationId: 'val-mock-1',
    address: '142 Maple Street, Boston, MA 02116',
    estimatedValue: 845000,
    valueLow: 820000,
    valueHigh: 870000,
    confidence: 'high',
    requestedAt: '2026-05-12T18:00:00Z',
    deliveredAt: '2026-05-12T18:01:30Z',
    source: 'speculo-avm',
  },
  {
    valuationId: 'val-mock-2',
    address: '89 Elm Street, Boston, MA 02116',
    estimatedValue: 795000,
    valueLow: 770000,
    valueHigh: 820000,
    confidence: 'medium',
    requestedAt: '2026-05-10T14:00:00Z',
    deliveredAt: '2026-05-10T14:02:00Z',
    source: 'speculo-avm',
  },
];
